import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
const SILK = [0.4, 0, 0.2, 1] as const;
const BUTTER = [0.22, 1, 0.36, 1] as const;
function PreloaderCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let animationId: number;
    let time = 0;
    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resize();
    window.addEventListener('resize', resize);
    const draw = () => {
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      ctx.clearRect(0, 0, w, h);
      const spacing = 40;
      const cols = Math.ceil(w / spacing) + 1;
      const rows = Math.ceil(h / spacing) + 1;
      const cx = w / 2;
      const cy = h / 2;
      for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
          const x = i * spacing;
          const y = j * spacing;
          const dist = Math.sqrt((x - cx) ** 2 + (y - cy) ** 2);
          const maxDist = Math.sqrt(cx ** 2 + cy ** 2);
          const fade = Math.max(0, 1 - dist / maxDist);
          const wave = Math.sin(time * 0.8 + i * 0.3 + j * 0.2) * 0.5 + 0.5;
          const size = 0.8 + wave * 1.2;
          const alpha = fade * (0.04 + wave * 0.08);
          ctx.beginPath();
          ctx.arc(x, y, size, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(250, 250, 250, ${alpha})`;
          ctx.fill();
        }
      }
      time += 0.012;
      animationId = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationId);
    };
  }, []);
  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" data-id="element-220" />;
}
const PHRASE = 'smooth operator';
function StaggeredText({
  phase
}: {
  phase: string;
}) {
  const letters = PHRASE.split('');
  return <div className="flex items-center justify-center" data-id="element-221">
      {letters.map((char, i) => <motion.span key={i} initial={{
      opacity: 0
    }} animate={{
      opacity: phase === 'complete' ? 0 : 1
    }} transition={{
      opacity: {
        duration: phase === 'complete' ? 0.3 : 0.4,
        delay: phase === 'complete' ? 0.02 * (letters.length - i) : 0.3 + i * 0.055,
        ease: SILK
      }
    }} className="text-xl md:text-3xl font-display font-bold tracking-tight text-primary" style={{
      display: 'inline-block',
      whiteSpace: char === ' ' ? 'pre' : 'normal',
      minWidth: char === ' ' ? '0.3em' : undefined
    }} data-id="element-222">
          {char}
        </motion.span>)}
    </div>;
}
export function Preloader() {
  const [isLoading, setIsLoading] = useState(true);
  const [phase, setPhase] = useState<'orbit' | 'reveal' | 'complete'>('orbit');
  useEffect(() => {
    // Reveal at 1.4s (letters are done appearing)
    const revealTimer = setTimeout(() => setPhase('reveal'), 1400);
    // Complete at 2.1s
    const completeTimer = setTimeout(() => setPhase('complete'), 2100);
    // Dismiss at 2.7s
    const dismissTimer = setTimeout(() => setIsLoading(false), 2700);
    return () => {
      clearTimeout(revealTimer);
      clearTimeout(completeTimer);
      clearTimeout(dismissTimer);
    };
  }, []);
  return <AnimatePresence data-id="element-223">
      {isLoading && <motion.div initial={{
      opacity: 1
    }} exit={{
      opacity: 0,
      transition: {
        duration: 0.6,
        ease: SILK
      }
    }} className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-background text-primary overflow-hidden" data-id="element-224">
          <PreloaderCanvas data-id="element-225" />

          <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(circle at center, transparent 30%, hsl(0 0% 4%) 80%)'
      }} data-id="element-226" />

          {/* Ripples on complete */}
          <AnimatePresence data-id="element-227">
            {phase === 'complete' && <>
                {[0, 1, 2].map(i => <motion.div key={`ripple-${i}`} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/12 pointer-events-none" initial={{
            width: 0,
            height: 0,
            opacity: 0.4
          }} animate={{
            width: ['0px', '700px'],
            height: ['0px', '700px'],
            opacity: [0.25, 0]
          }} transition={{
            duration: 1.2,
            delay: i * 0.12,
            ease: BUTTER
          }} data-id="element-228" />)}
              </>}
          </AnimatePresence>

          <div className="relative z-10 flex flex-col items-center" data-id="element-229">
            {/* Orbit */}
            <motion.div initial={{
          scale: 0.5,
          opacity: 0
        }} animate={{
          scale: phase === 'complete' ? 1.8 : 1,
          opacity: phase === 'complete' ? 0 : 1
        }} transition={{
          scale: {
            duration: phase === 'complete' ? 0.8 : 0.8,
            ease: BUTTER
          },
          opacity: {
            duration: phase === 'complete' ? 0.5 : 0.6,
            ease: SILK
          }
        }} className="relative w-36 h-36 md:w-48 md:h-48 mb-8" data-id="element-230">
              <div className="absolute inset-0 rounded-full" style={{
            background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 55%)',
            filter: 'blur(20px)',
            transform: 'scale(1.5)'
          }} data-id="element-231" />

              <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full" data-id="element-232">
                <motion.circle cx="100" cy="100" r="92" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="0.5" strokeDasharray="578" initial={{
              strokeDashoffset: 578
            }} animate={{
              strokeDashoffset: 0
            }} transition={{
              duration: 1.2,
              delay: 0.1,
              ease: BUTTER
            }} data-id="element-233" />
                <motion.circle cx="100" cy="100" r="60" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="0.3" strokeDasharray="377" initial={{
              strokeDashoffset: 377
            }} animate={{
              strokeDashoffset: 0
            }} transition={{
              duration: 1,
              delay: 0.3,
              ease: BUTTER
            }} data-id="element-234" />
                <motion.circle cx="100" cy="100" r="30" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.3" strokeDasharray="188" initial={{
              strokeDashoffset: 188
            }} animate={{
              strokeDashoffset: 0
            }} transition={{
              duration: 0.8,
              delay: 0.5,
              ease: BUTTER
            }} data-id="element-235" />
              </svg>

              {/* Outer comet */}
              <motion.svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full" animate={{
            rotate: 360
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-236">
                <defs data-id="element-237">
                  <linearGradient id="preTrail1" gradientUnits="userSpaceOnUse" x1="8" y1="100" x2="100" y2="8" data-id="element-238">
                    <stop offset="0%" stopColor="rgba(255,255,255,0)" data-id="element-239" />
                    <stop offset="60%" stopColor="rgba(255,255,255,0.15)" data-id="element-240" />
                    <stop offset="100%" stopColor="rgba(255,255,255,0.45)" data-id="element-241" />
                  </linearGradient>
                </defs>
                <circle cx="100" cy="100" r="92" fill="none" stroke="url(#preTrail1)" strokeWidth="1.5" strokeDasharray="145 433" strokeLinecap="round" data-id="element-242" />
                <circle cx="100" cy="8" r="3" fill="rgba(255,255,255,0.7)" data-id="element-243">
                  <animate attributeName="r" values="2.5;3.5;2.5" dur="1.5s" repeatCount="indefinite" data-id="element-244" />
                </circle>
              </motion.svg>

              {/* Middle comet */}
              <motion.svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full" animate={{
            rotate: -360
          }} transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-245">
                <defs data-id="element-246">
                  <linearGradient id="preTrail2" gradientUnits="userSpaceOnUse" x1="160" y1="100" x2="100" y2="160" data-id="element-247">
                    <stop offset="0%" stopColor="rgba(255,255,255,0)" data-id="element-248" />
                    <stop offset="70%" stopColor="rgba(255,255,255,0.1)" data-id="element-249" />
                    <stop offset="100%" stopColor="rgba(255,255,255,0.3)" data-id="element-250" />
                  </linearGradient>
                </defs>
                <circle cx="100" cy="100" r="60" fill="none" stroke="url(#preTrail2)" strokeWidth="1" strokeDasharray="95 282" strokeLinecap="round" data-id="element-251" />
                <circle cx="160" cy="100" r="2" fill="rgba(255,255,255,0.5)" data-id="element-252">
                  <animate attributeName="r" values="1.5;2.5;1.5" dur="2s" repeatCount="indefinite" data-id="element-253" />
                </circle>
              </motion.svg>

              {/* Inner comet */}
              <motion.svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full" animate={{
            rotate: 360
          }} transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-254">
                <defs data-id="element-255">
                  <linearGradient id="preTrail3" gradientUnits="userSpaceOnUse" x1="100" y1="130" x2="70" y2="100" data-id="element-256">
                    <stop offset="0%" stopColor="rgba(255,255,255,0)" data-id="element-257" />
                    <stop offset="80%" stopColor="rgba(255,255,255,0.08)" data-id="element-258" />
                    <stop offset="100%" stopColor="rgba(255,255,255,0.2)" data-id="element-259" />
                  </linearGradient>
                </defs>
                <circle cx="100" cy="100" r="30" fill="none" stroke="url(#preTrail3)" strokeWidth="0.7" strokeDasharray="47 141" strokeLinecap="round" data-id="element-260" />
                <circle cx="100" cy="130" r="1.5" fill="rgba(255,255,255,0.35)" data-id="element-261">
                  <animate attributeName="r" values="1;2;1" dur="3s" repeatCount="indefinite" data-id="element-262" />
                </circle>
              </motion.svg>

              {/* Center core */}
              <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" initial={{
            scale: 0,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            duration: 0.6,
            delay: 0.3,
            ease: BUTTER
          }} data-id="element-263">
                <div className="w-5 h-5 rounded-full" style={{
              background: 'radial-gradient(circle, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.15) 50%, transparent 100%)',
              boxShadow: '0 0 20px 6px rgba(255,255,255,0.1), 0 0 40px 12px rgba(255,255,255,0.04)'
            }} data-id="element-264" />
              </motion.div>

              {/* Center pulse */}
              <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{
            border: '1px solid rgba(255,255,255,0.1)'
          }} animate={{
            width: [0, 50, 65],
            height: [0, 50, 65],
            opacity: [0, 0.3, 0]
          }} transition={{
            duration: 2.5,
            delay: 0.5,
            repeat: Infinity,
            ease: 'easeOut'
          }} data-id="element-265" />
            </motion.div>

            {/* Staggered letter reveal — no shifting, just fade */}
            <StaggeredText phase={phase} data-id="element-266" />
          </div>
        </motion.div>}
    </AnimatePresence>;
}